import isMobile from 'ismobilejs'

const PRODUCTS_PAGE_SIZE = {
  default: 48,
  mobile: 12,
}

const getPageSize = (mobile) => {
  if (import.meta.server) {
    return PRODUCTS_PAGE_SIZE.default
  }

  return PRODUCTS_PAGE_SIZE[mobile ? 'mobile' : 'default']
}

export const getParamsFromFilters = (
  filters,
  page,
  pageSize,
  ordering,
  imageSearch,
) => {
  const params = {}
  if (page) {
    params.page = page
  }
  if (pageSize) {
    params.page_size = pageSize
  }
  if (ordering) {
    params.ordering = ordering
  }
  if (imageSearch) {
    params.image_search = imageSearch
  }

  filters.forEach((filter) => {
    if (filter.category === 'is_offer') {
      const paramKey = filter.category + '__in'
      if (!params[paramKey]) {
        params[paramKey] = filter.key_as_string
      } else {
        params[paramKey] += '__' + filter.key_as_string
      }
    } else if (filter.category === 'notify-me') {
      params.notify_me = 'true'
    } else if (filter.category === 'personalized') {
      params.personalized = 'true'
    } else if (filter.category === 'search') {
      const paramKey = filter.category
      if (!params[paramKey]) {
        params[paramKey] = filter.key
      } else {
        params[paramKey] += '__' + filter.key
      }
    } else if (filter.category === 'custom_price') {
      if (filter.from) {
        params.price_excl_tax__gte = filter.from
      }
      if (filter.to !== undefined && filter.to !== null) {
        params.price_excl_tax__lte = filter.to
      }
    } else if (filter.category === 'custom_points') {
      if (filter.from) {
        params.expert_rating_range__gte = filter.from
      }
      if (filter.to) {
        params.expert_rating_range__lte = filter.to
      }
    } else if (filter.category === 'alcohol_percent') {
      if (filter.from) {
        params.alcohol_percent__gte = filter.from
      }
      if (filter.to) {
        params.alcohol_percent__lte = filter.to
      }
    } else if (filter.category === 'drinking_window') {
      if (filter.from) {
        params.drinking_window_end__gte = filter.from
      }
      if (filter.to) {
        params.drinking_window_start__lte = filter.to
      }
    } else if (filter.category === 'flavor_profile') {
      if (filter.from) {
        params[`${filter.key}_scale__gte`] = filter.from
      }
      if (filter.to !== undefined && filter.to !== null) {
        params[`${filter.key}_scale__lte`] = filter.to
      }
    } else {
      let paramKey = filter.category
      if (!filter.category.endsWith('__in')) {
        paramKey = filter.category + '__in'
      }
      if (!params[paramKey]) {
        params[paramKey] = filter.key
      } else {
        params[paramKey] += '__' + filter.key
      }
    }
  })

  return params
}

export const useProductResults = defineStore('productResults', () => {
  const { $store } = useNuxtApp()
  const metadata = useMetadata()
  const searchProducts = ref([])
  const categoryCircles = ref([])
  const resultsCount = ref(0)
  const pageSize = ref(PRODUCTS_PAGE_SIZE.default)
  const thisOccasion = ref({})
  const productIDInView = ref(undefined)
  const carouselInView = ref(undefined)
  const searchingProducts = ref(false)

  const totalPages = computed(() =>
    Math.ceil(resultsCount.value / pageSize.value),
  )

  const orderingFromUrl = (route, selectedFilters) => {
    // Occasions have a default sorting field,
    let defaultSorting =
      thisOccasion.value && thisOccasion.value.default_sorting
    if (!defaultSorting && metadata.occasions) {
      // Check if we have multiple occasions in the filter
      const occasionFilter = selectedFilters.find(
        (x) => x.category === 'occasions',
      )
      if (occasionFilter) {
        const firstOccasion = metadata.occasions
          .reduce((prev, curr) => [...prev, ...curr.items], [])
          .find((x) => x.slug === occasionFilter.key)
        defaultSorting = firstOccasion ? firstOccasion.default_sorting : null
      }
    }
    if (!defaultSorting) {
      const isOffer =
        route.params.category === 'is_offer' && route.params.slug === 'true'
      defaultSorting = isOffer ? '-start_datetime' : 'personalized'
    }
    return route.query.ordering || defaultSorting
  }

  const setCategoryCircles = (payload) => {
    if (payload && !Array.isArray(payload)) {
      payload = payload.data
    }
    categoryCircles.value = payload
  }

  async function fetchProducts($axios, route, filters) {
    if (searchingProducts.value) {
      return
    }
    searchingProducts.value = true
    let mobile = false

    if (!import.meta.server) {
      mobile = isMobile(window.navigator).phone
    }

    const params = getParamsFromFilters(
      filters,
      route.query.page || 1,
      getPageSize(mobile),
      orderingFromUrl(route, filters),
      route.query.image_search,
    )

    const pptoMatch = route.path.match(/.*add-to-order\/([^/]+)/i)
    if (pptoMatch) {
      params.order_id = pptoMatch[1]
    }

    if (
      route.params.category === 'search' &&
      route.params.slug &&
      route.params.slug.length
    ) {
      $store.dispatch('setSearchOpen', true)
    }

    const resp = await $axios.get('/api/catalog/search/', { params })
    setCategoryCircles(resp.data.category_circles_nav_results)
    searchProducts.value = resp.data.results
    resultsCount.value = resp.data.count
    searchingProducts.value = false
  }
  function clearOccasion() {
    thisOccasion.value = {}
  }
  async function fetchOccasion($axios, route) {
    clearOccasion()
    let occasionSlug = null

    if (route.params.category === 'occasion') {
      occasionSlug = route.params.slug
    } else if (
      route.query.occasions__in &&
      route.query.occasions__in.split(/,|__/).length === 1
    ) {
      // Only set the occasion if there is a single occasion
      occasionSlug = route.query.occasions__in
    } else if (
      route.query.occasions &&
      route.query.occasions.split(/,|__/).length === 1
    ) {
      // Only set the occasion if there is a single occasion
      occasionSlug = route.query.occasions
    }

    if (occasionSlug) {
      const resp = await $axios.get(
        '/api/catalog/retrieve/occasion/' + occasionSlug + '/',
      )
      thisOccasion.value = resp.data
    }
  }

  function setProductIDInView(productID) {
    productIDInView.value = productID
  }
  function setCarouselInView(productID) {
    carouselInView.value = productID
  }

  return {
    searchProducts,
    categoryCircles,
    resultsCount,
    pageSize,
    thisOccasion,
    productIDInView,
    carouselInView,
    totalPages,
    searchingProducts,
    orderingFromUrl,
    fetchProducts,
    fetchOccasion,
    clearOccasion,
    setProductIDInView,
    setCarouselInView,
  }
})
